import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
// import axios from "axios";
import axios from "../../context/axios";
import Swal from "sweetalert2";
import CircularProgress from "@mui/material/CircularProgress";

const Datatable = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchReservations = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get("/reservations");
        setList(res.data.data || []);
      } catch (error) {
        console.error("Failed to fetch reservations:", error);
        Swal.fire("Error", "Failed to load reservations", "error");
      }
      setIsLoading(false);
    };

    fetchReservations();
  }, []);

  const handleDelete = async (id) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure you want to delete this reservation?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });

    if (confirmResult.isConfirmed) {
      try {
        setIsLoading(true);
        await axios.delete(`/reservations/${id}`);
        setList(list.filter((item) => item._id !== id));
        Swal.fire("Deleted!", "Reservation has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete reservation", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleView = async (id) => {
    try {
      const reservationData = await axios.get(`/reservations/${id}`);
      navigate("/reservation/view", { state: reservationData.data });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load reservation details", "error");
    }
  };

  const columns = [
    { field: "fullName", headerName: "Full Name", width: 150 },
    {
      field: "userName",
      headerName: "User Name",
      width: 150,
      valueGetter: (params) => params.row.userId?.name || "N/A",
    },
    {
      field: "tourTitle",
      headerName: "Tour Title",
      width: 200,
      valueGetter: (params) => params.row.tourId?.title || "N/A",
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 130,
    },
    {
      field: "date",
      headerName: "Tour Date",
      width: 160,
      valueGetter: (params) =>
        new Date(params.row.date).toLocaleDateString(),
    },
    {
      field: "createdAt",
      headerName: "Booked At",
      width: 160,
      valueGetter: (params) =>
        new Date(params.row.createdAt).toLocaleDateString(),
    },
    {
      field: "specialRequest",
      headerName: "Special Request",
      width: 200,
    },
    {
      field: "guests",
      headerName: "Guests (A/C/I)",
      width: 150,
      valueGetter: (params) => {
        const g = params.row.guests || {};
        return `A:${g.adult || 0} / C:${g.child || 0} / I:${g.infant || 0}`;
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="cellAction">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded mr-2"
            onClick={() => handleView(params.row._id)}
          >
            View
          </button>
          <button
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded"
            onClick={() => handleDelete(params.row._id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const filteredList = useMemo(() => {
    if (!searchQuery) return list;
    const regex = new RegExp(searchQuery.trim(), "i");
    return list.filter((item) => {
      const searchString =
        (item.userId?.name || "") +
        " " +
        (item.tourId?.title || "") +
        " " +
        (item.fullName || "") +
        " " +
        (item.phone || "") +
        " " +
        (item.email || "");
      return regex.test(searchString);
    });
  }, [list, searchQuery]);

  return (
    <div className="flex flex-col col-span-2 lg:px-32 px-8 pt-3 pb-8 gap-5">
      <div className="flex md:justify-end">
        <input
          type="text"
          placeholder="Search reservations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border-4 rounded py-2 px-4 w-full lg:mt-0 mt-3"
        />
      </div>

      <div className="datatable" style={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredList}
          columns={columns}
          pageSize={9}
          rowsPerPageOptions={[9]}
          checkboxSelection
          loading={isLoading}
          getRowId={(row) => row._id}
          loadingOverlay={
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default Datatable;